<template>
  <div class="ai-assistant-page">
    <div class="ai-header">
      <div class="ai-title">
        <h1>AI智能助手</h1>
        <p>专业无人机技术学习助手，随时为您解答疑问</p>
      </div>
      
    </div>

    <div class="ai-body">
      <!-- 聊天区域 -->
      <div class="chat-container">
        <div class="chat-messages" ref="chatMessages">
          <!-- AI欢迎消息 -->
          <div class="message ai-message">
            <div class="message-avatar ai-avatar">🤖</div>
            <div class="message-content">
              <div class="message-sender">AI助手</div>
              <div class="message-text">
                <p>您好！我是无人机学习AI助手，很高兴为您服务！我可以帮助您解答关于无人机技术的各种问题，包括：</p>
                <ul>
                  <li>无人机基础知识</li>
                  <li>飞行原理与操作</li>
                  <li>安全规范与法规</li>
                  <li>故障排除与维护</li>
                </ul>
                <p>请随时向我提问，我会尽力为您提供准确、详细的解答。</p>
              </div>
              <div class="message-time">{{ getCurrentTime() }}</div>
            </div>
          </div>

          <!-- 聊天消息 -->
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="message"
            :class="msg.type === 'user' ? 'user-message' : 'ai-message'"
            :data-message-id="msg.id"
          >
            <div class="message-avatar" :class="msg.type === 'user' ? 'user-avatar' : 'ai-avatar'">
              {{ msg.type === 'user' ? '👤' : '🤖' }}
            </div>
            <div class="message-content">
              <div class="message-sender">{{ msg.type === 'user' ? '我' : 'AI助手' }}</div>
              <div class="message-text" v-html="formatMessage(msg.content)"></div>
              <div class="message-actions" v-if="msg.type === 'ai'">
                <button class="btn-icon" v-if="!msg.isSpeaking" title="开始播报" @click="startSpeaking(msg)">🔊</button>
                <button class="btn-icon" v-else title="停止播报" @click="stopSpeaking(msg)">⏹️</button>
                <button class="btn-icon" title="复制内容" @click="copyMessage(msg.content)">📋</button>
                <button class="btn-icon" :class="{ liked: msg.liked }" title="点赞" @click="msg.liked = !msg.liked">👍</button>
              </div>
              <div class="message-time">{{ msg.time }}</div>
            </div>
          </div>

          <!-- 加载指示器 -->
          <div class="message ai-message" v-if="isLoading">
            <div class="message-avatar ai-avatar">🤖</div>
            <div class="message-content">
              <div class="message-sender">AI助手</div>
              <div class="message-text loading">
                <span class="loading-dot"></span>
                <span class="loading-dot"></span>
                <span class="loading-dot"></span>
                正在思考中...
              </div>
            </div>
          </div>
        </div>

        <!-- 输入区域 -->
        <div class="chat-input">
          <input
            type="text"
            class="message-input"
            v-model="inputMessage"
            placeholder="输入您的问题..."
            @keyup.enter="sendMessage"
          >
          <button 
            class="btn-clear" 
            @click="showClearConfirm = true" 
            title="清空聊天"
            :disabled="messages.length === 0"
          >
            清空
          </button>
          <button class="send-btn" @click="sendMessage">发送</button>
        </div>
      </div>

      <!-- 清空确认弹窗 -->
      <div v-if="showClearConfirm" class="modal-overlay">
        <div class="modal-content">
          <h3>确认清空</h3>
          <p>确定要清空所有聊天记录吗？此操作不可恢复。</p>
          <div class="modal-buttons">
            <button class="btn-cancel" @click="showClearConfirm = false">取消</button>
            <button class="btn-confirm" @click="confirmClearMessages">确认</button>
          </div>
        </div>
      </div>

      <!-- 侧边栏 -->
      <div class="ai-sidebar">
        <!-- 快速提问 -->
        <div class="sidebar-card">
          <h3>快速提问</h3>
          <div class="quick-questions">
            <button
              v-for="(q, index) in quickQuestions"
              :key="index"
              class="quick-question-btn"
              @click="askQuickQuestion(q)"
            >{{ q }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wrjAiAnswer, getAiTalkHistory, clearAiTalkHistory } from '@/api/ai';

export default {
  name: 'AiAssistant',
  data() {
    return {
      inputMessage: '',
      isLoading: false,
      messages: [],
      quickQuestions: [
        '无人机的分类有哪些？',
        '如何获得无人机驾驶证？',
        '无人机飞行的安全注意事项',
        '无人机电池的保养方法',
        '无人机故障如何排查？'
      ],
      showClearConfirm: false,
      abortController: null
    }
  },
  created() {
    // 获取AI问答历史记录
    this.fetchAIHistory();
  },
  beforeUnmount() {
    // 组件销毁前停止所有语音播报
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      console.log('组件销毁，停止所有语音播报');
    }
  },
  methods: {
    // 获取当前时间
    getCurrentTime() {
      const now = new Date()
      return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    },

    // 格式化消息（支持换行和基本样式）
    formatMessage(content) {
      // 确保 content 是字符串类型
      if (typeof content !== 'string') {
        return String(content)
      }
      return content
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    },

    // 发送消息
    sendMessage() {
      if (!this.inputMessage.trim()) return

      const message = this.inputMessage.trim()
      const now = this.getCurrentTime()

      // 添加用户消息
      this.messages.push({
        type: 'user',
        content: message,
        time: now,
        liked: false
      })

      this.inputMessage = ''
      this.isLoading = true

      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })

      // 构建历史文本
      const historyText = this.messages
        .filter(msg => msg.type === 'user' || msg.type === 'ai')
        .map(msg => `${msg.type === 'user' ? '用户' : 'AI'}: ${msg.content}`)
        .join('\n')

      // 取消之前的请求
      if (this.abortController) {
        this.abortController.abort()
      }

      // 调用AI问答接口
      const messageId = 'ai-message-' + Date.now()

      wrjAiAnswer(message, historyText)
        .then((response) => {
          console.log('AI接口返回:', response.data);
          this.isLoading = false;
          
          if (response.data.code === 200 && response.data.data) {
            // 解析接口返回的answer
            const answer = response.data.data.answer || response.data.data.text || '';
            
            // 创建AI消息
            const aiMessage = {
              id: messageId,
              type: 'ai',
              content: answer,
              time: this.getCurrentTime(),
              liked: false,
              isSpeaking: false
            };
            this.messages.push(aiMessage);
            
            // 滚动到底部
            this.$nextTick(() => {
              this.scrollToBottom();
            });
            
            // 自动语音播报AI回答
            this.speakMessage(aiMessage.content, aiMessage);
          } else {
            // 接口返回异常
            console.warn('AI接口返回数据异常:', response.data);
            this.messages.push({
              id: messageId,
              type: 'ai',
              content: 'AI助手暂时无法回答您的问题，请稍后重试。',
              time: this.getCurrentTime(),
              liked: false
            });
          }
        })
        .catch((error) => {
          // 错误处理
          console.error('获取AI回复失败:', error);
          this.isLoading = false;
          
          // 网络错误时的默认回复
          const errorMessageId = 'ai-message-error-' + Date.now();
          this.messages.push({
            id: errorMessageId,
            type: 'ai',
            content: '网络错误，请检查您的网络连接后重试。',
            time: this.getCurrentTime(),
            liked: false
          });
          
          // 滚动到错误消息
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        });
    },

    // 滚动到底部
    scrollToBottom() {
      const container = this.$refs.chatMessages
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    // 滚动到AI消息的开始处（用户消息下方）
    scrollToAiMessageStart(messageId) {
      // 延迟执行，确保消息完全渲染
      setTimeout(() => {
        // 找到AI消息元素
        const aiMessageElement = document.querySelector(`[data-message-id="${messageId}"]`)
        if (aiMessageElement) {
          // 找到用户消息元素（AI消息的前一条）
          const allMessages = document.querySelectorAll('.message')
          let userMessageElement = null
          
          for (let i = 0; i < allMessages.length; i++) {
            if (allMessages[i] === aiMessageElement && i > 0) {
              userMessageElement = allMessages[i - 1]
              break
            }
          }
          
          // 如果找到用户消息，滚动到用户消息下方
          const targetElement = userMessageElement || aiMessageElement
          const container = this.$refs.chatMessages
          
          if (container && targetElement) {
            // 计算目标元素相对于容器顶部的位置
            const targetRect = targetElement.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()
            
            // 计算需要滚动的距离，留20px的空间，并向上偏移60px
            const scrollDistance = targetRect.bottom - containerRect.top - 20 - 60
            
            // 滚动到目标位置
            container.scrollTop += scrollDistance
            console.log('已滚动到用户消息下方:', messageId, '滚动距离:', scrollDistance)
          }
        }
      }, 100) // 延迟100ms，确保消息完全渲染
    },

    // 快速提问
    askQuickQuestion(question) {
      this.inputMessage = question
      this.sendMessage()
    },

    // 语音播报
    speakMessage(content, msg = null) {
      console.log('开始语音播报:', content)
      // 简单测试语音合成是否可用
      if ('speechSynthesis' in window) {
        console.log('浏览器支持语音合成')
        console.log('语音合成状态:', speechSynthesis.status)
        // 移除HTML标签
        const text = content.replace(/<[^>]*>/g, '')
        console.log('处理后的文本:', text)
        
        // 确保语音合成服务已初始化
        if (speechSynthesis.getVoices().length === 0) {
          console.log('等待语音合成服务初始化...')
          speechSynthesis.onvoiceschanged = () => {
            console.log('语音合成服务已初始化')
            this.doSpeak(text, msg)
          }
        } else {
          this.doSpeak(text, msg)
        }
      } else {
        console.log('浏览器不支持语音合成')
        alert('您的浏览器不支持语音播报功能')
      }
    },
    
    // 开始播报
    startSpeaking(msg) {
      console.log('开始播报消息:', msg.content)
      // 标记消息为正在播报状态
      msg.isSpeaking = true
      // 调用语音播报方法
      this.speakMessage(msg.content, msg)
    },
    
    // 停止播报
    stopSpeaking(msg) {
      console.log('停止播报消息')
      // 标记消息为停止播报状态
      msg.isSpeaking = false
      // 取消所有语音播报
      speechSynthesis.cancel()
      console.log('已停止所有语音播报')
    },
    
    // 执行语音播报
    doSpeak(text, msg) {
      console.log('执行语音播报:', text)
      
      // 检查可用的语音包
      const voices = speechSynthesis.getVoices()
      console.log('可用语音包数量:', voices.length)
      console.log('可用语音包:', voices.map(v => ({ name: v.name, lang: v.lang })))
      
      // 尝试找到中文语音包
      let chineseVoice = voices.find(v => v.lang.includes('zh'))
      console.log('找到的中文语音包:', chineseVoice)
      
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'zh-CN'
      utterance.rate = 1
      
      // 如果找到中文语音包，使用它
      if (chineseVoice) {
        utterance.voice = chineseVoice
        console.log('使用语音包:', chineseVoice.name)
      } else {
        console.log('未找到中文语音包，使用默认语音')
      }
      
      // 监听播放事件
      utterance.onstart = () => {
        console.log('语音播报开始')
        if (msg) msg.isSpeaking = true
      }
      utterance.onend = () => {
        console.log('语音播报结束')
        if (msg) msg.isSpeaking = false
      }
      utterance.onerror = (event) => {
        console.error('语音播报错误:', event)
        if (msg) msg.isSpeaking = false
      }
      utterance.onpause = () => console.log('语音播报暂停')
      utterance.onresume = () => console.log('语音播报恢复')
      
      console.log('准备播放...')
      console.log('语音设置:', {
        text: text.substring(0, 50) + '...',
        lang: utterance.lang,
        rate: utterance.rate,
        voice: utterance.voice ? utterance.voice.name : 'default'
      })
      
      // 尝试先取消所有之前的语音，然后播放
      speechSynthesis.cancel()
      console.log('已取消之前的语音')
      
      speechSynthesis.speak(utterance)
      console.log('播放已启动')
    },

    // 复制内容
    copyMessage(content) {
      const text = content.replace(/<[^>]*>/g, '')
      
      // 检查 navigator.clipboard 是否可用
      if (navigator.clipboard && window.isSecureContext) {
        // 使用现代 API
        navigator.clipboard.writeText(text).then(() => {
          alert('已复制到剪贴板')
        }).catch(() => {
          // 现代 API 失败，尝试降级方案
          this.fallbackCopyTextToClipboard(text)
        })
      } else {
        // 降级到传统方案
        this.fallbackCopyTextToClipboard(text)
      }
    },
    
    // 降级复制方案
    fallbackCopyTextToClipboard(text) {
      try {
        // 创建一个临时的 textarea 元素
        const textArea = document.createElement('textarea')
        textArea.value = text
        
        // 设置样式使其不可见
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        
        // 选择并复制文本
        textArea.focus()
        textArea.select()
        
        // 执行复制命令
        const successful = document.execCommand('copy')
        if (successful) {
          alert('已复制到剪贴板')
        } else {
          alert('复制失败')
        }
      } catch (err) {
        console.error('复制失败:', err)
        alert('复制失败')
      } finally {
        // 清理临时元素
        const textArea = document.querySelector('textarea[style*="left: -999999px"]')
        if (textArea) {
          document.body.removeChild(textArea)
        }
      }
    },

    // 语音输入
    startVoiceInput() {
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        const recognition = new SpeechRecognition()
        recognition.lang = 'zh-CN'
        recognition.continuous = false

        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript
          this.inputMessage = transcript
        }

        recognition.onerror = (event) => {
          console.error('语音识别错误:', event.error)
        }

        recognition.start()
      } else {
        alert('您的浏览器不支持语音输入功能')
      }
    },

    // 确认清空聊天
    async confirmClearMessages() {
      // 停止所有语音播报
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
        console.log('已停止所有语音播报');
      }
      
      try {
        const res = await clearAiTalkHistory();
        console.log('清空历史记录接口返回:', res.data);
        if (res.data.code === 200) {
          // 清空本地消息
          this.messages = [];
          console.log('历史记录已清空');
        } else {
          console.warn('清空历史记录失败，接口返回错误:', res.data);
          // 接口返回错误时，不清空本地消息
        }
      } catch (error) {
        console.error('清空历史记录失败:', error);
        // 接口调用失败时，不清空本地消息
      } finally {
        // 关闭确认弹窗
        this.showClearConfirm = false;
      }
    },
    
    // 解析AI返回的内容（处理可能的JSON字符串）
    parseAIContent(content) {
      if (!content) return '';
      
      // 尝试解析JSON字符串
      try {
        // 检查是否是JSON格式
        if (content.trim().startsWith('{')) {
          let parsed = JSON.parse(content);
          
          // 循环解析，处理多重嵌套的JSON字符串
          while (parsed.data) {
            // 如果 data 是字符串且是JSON格式，继续解析
            if (typeof parsed.data === 'string' && parsed.data.trim().startsWith('{')) {
              parsed = JSON.parse(parsed.data);
            } else {
              break;
            }
          }
          
          // 解析完成后，提取 answer 字段
          if (parsed.answer) {
            return parsed.answer;
          }
          // 如果有 data 字段且是对象，提取 answer
          if (typeof parsed.data === 'object' && parsed.data.answer) {
            return parsed.data.answer;
          }
          // 如果 data 字段存在，返回 data
          if (parsed.data) {
            return typeof parsed.data === 'string' ? parsed.data : JSON.stringify(parsed.data);
          }
        }
      } catch (e) {
        // 不是有效的JSON，直接返回原内容
        console.log('内容不是JSON格式，直接返回:', content.substring(0, 50));
      }
      
      return content;
    },

    // 获取AI问答历史记录
    async fetchAIHistory() {
      try {
        const res = await getAiTalkHistory();
        console.log('AI历史记录接口返回:', res.data);
        if (res.data.code === 200 && res.data.data) {
          // 清空现有消息
          this.messages = [];
          
          // 处理历史记录数据
          const historyData = res.data.data;
          console.log('历史记录原始数据条数:', historyData.length);
          if (Array.isArray(historyData)) {
            // 按时间顺序添加消息
            historyData.forEach((item, index) => {
              console.log(`处理第${index + 1}条记录:`, item);
              // 根据 senderType 区分用户消息和 AI 消息
              if (item.senderType === '0') {
                // 用户消息
                this.messages.push({
                  type: 'user',
                  content: this.parseAIContent(item.questions),
                  time: this.formatTime(item.createTime),
                  liked: false
                });
                console.log(`第${index + 1}条是用户消息，当前消息数组长度:`, this.messages.length);
              } else if (item.senderType === '1') {
                // AI 消息
                this.messages.push({
                  type: 'ai',
                  content: this.parseAIContent(item.questions),
                  time: this.formatTime(item.createTime),
                  liked: false,
                  isSpeaking: false
                });
                console.log(`第${index + 1}条是AI消息，当前消息数组长度:`, this.messages.length);
              } else {
                // 处理未知类型的消息
                console.warn('第' + (index + 1) + '条记录的senderType无效:', item.senderType, '完整数据:', item);
              }
            });
            console.log('最终消息数组长度:', this.messages.length);
          }
        } else {
          console.warn('接口返回数据异常:', res.data);
        }
      } catch (error) {
        console.error('获取AI历史记录失败:', error);
      } finally {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    
    // 格式化时间
    formatTime(timeString) {
      if (!timeString) return this.getCurrentTime();
      
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return this.getCurrentTime();
      
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }
  }
}
</script>

<style scoped>
.ai-assistant-page {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.ai-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.ai-title h1 {
  font-size: 32px;
  color: #333;
  margin-bottom: 10px;
}

.ai-title p {
  color: #666;
  font-size: 16px;
}

.ai-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  background: #d4edda;
  border-radius: 25px;
  color: #155724;
  font-weight: 600;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #28a745;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.ai-body {
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
}

.chat-container {
  background: #f8f9fa;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  height: 600px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.message {
  display: flex;
  gap: 15px;
  max-width: 80%;
}

.user-message {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.ai-message {
  align-self: flex-start;
}

.message-avatar {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.user-avatar {
  background: #667eea;
  color: white;
}

.ai-avatar {
  background: #28a745;
  color: white;
}

.message-content {
  background: white;
  border-radius: 15px;
  padding: 15px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.user-message .message-content {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.message-sender {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  opacity: 0.8;
}

.message-text {
  line-height: 1.8;
  font-size: 15px;
}

.message-text p {
  margin-bottom: 12px;
}

.message-text p:last-child {
  margin-bottom: 0;
}

.message-text ul,
.message-text ol {
  margin: 10px 0;
  padding-left: 20px;
}

.message-text li {
  margin-bottom: 8px;
}

.message-text strong {
  font-weight: 600;
}

.message-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.user-message .message-actions {
  border-top-color: rgba(255, 255, 255, 0.3);
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: rgba(0, 0, 0, 0.1);
}

.btn-icon.liked {
  background: #28a745;
}

.message-time {
  font-size: 12px;
  margin-top: 10px;
  opacity: 0.7;
}

.loading {
  display: flex;
  align-items: center;
  gap: 5px;
}

.loading-dot {
  width: 8px;
  height: 8px;
  background: #667eea;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-dot:nth-child(1) { animation-delay: -0.32s; }
.loading-dot:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.chat-input {
  display: flex;
  gap: 10px;
  padding: 15px;
  background: white;
  border-top: 1px solid #e0e0e0;
  border-radius: 0 0 15px 15px;
}

.input-tools {
  display: flex;
  gap: 5px;
}

.tool-btn {
  width: 40px;
  height: 40px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  background: white;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tool-btn:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f0f4ff;
}

.message-input {
  flex: 1;
  padding: 10px 15px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.3s;
}

.message-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.send-btn {
  padding: 10px 25px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.send-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

.btn-clear {
  padding: 10px 20px;
  background: #e0e0e0;
  color: #666;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.btn-clear:hover {
  background: #d0d0d0;
  color: #333;
}

.ai-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.sidebar-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 15px;
  padding: 20px;
}

.sidebar-card h3 {
  font-size: 18px;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
}

.quick-questions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quick-question-btn {
  padding: 12px 15px;
  background: #f8f9fa;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  color: #333;
  transition: all 0.3s;
}

.quick-question-btn:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
  transform: translateX(5px);
}

@media (max-width: 1024px) {
  .ai-body {
    grid-template-columns: 1fr;
  }

  .ai-sidebar {
    order: -1;
  }

  .chat-container {
    height: 500px;
  }
}

@media (max-width: 768px) {
  .ai-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .ai-title h1 {
    font-size: 24px;
  }

  .message {
    max-width: 95%;
  }

  .chat-container {
    height: 450px;
  }

  .chat-input {
    flex-direction: column;
  }

  .input-tools {
    justify-content: center;
  }

  .message-input {
    width: 100%;
  }

  .send-btn {
    width: 100%;
  }
}

/* 清空确认弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-content h3 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 18px;
  color: #333;
  text-align: center;
}

.modal-content p {
  margin-bottom: 20px;
  color: #666;
  text-align: center;
  line-height: 1.5;
}

.modal-buttons {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.btn-cancel, .btn-confirm {
  flex: 1;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel {
  background-color: #f0f0f0;
  color: #333;
}

.btn-cancel:hover {
  background-color: #e0e0e0;
}

.btn-confirm {
  background-color: #409EFF;
  color: white;
}

.btn-confirm:hover {
  background-color: #66b1ff;
}

/* 禁用状态的清空按钮 */
.btn-clear:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-clear:disabled:hover {
  background-color: #e0e0e0;
  color: #666;
}
</style>
